import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

export default function ResetPassword() {
  const [searchParams] = useSearchParams()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const navigate = useNavigate()
  const { supabase } = useAuth()

  // Get email from URL (for display only)
  const emailFromUrl = searchParams.get('email')

  useEffect(() => {
    // Check if we have a valid reset token in the URL hash
    // Supabase sends it as #access_token=xxx&type=recovery
    const hashParams = new URLSearchParams(window.location.hash.substring(1))
    const type = hashParams.get('type')
    
    if (type !== 'recovery') {
      console.log('No recovery token found')
    }
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    // Validation
    if (password.length < 6) {
      setError('Password must be at least 6 characters long')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)

    try {
      // Use Supabase to update password
      // This works when user comes from the email link with the recovery token
      const { data, error: updateError } = await supabase.auth.updateUser({
        password: password
      })

      if (updateError) {
        console.error('Password update error:', updateError)
        
        if (updateError.message.includes('expired') || updateError.message.includes('invalid')) {
          setError('Reset link has expired or is invalid. Please request a new one.')
        } else {
          setError(updateError.message || 'Failed to reset password')
        }
        setLoading(false)
        return
      }

      console.log('Password updated successfully:', data)
      setSuccess(true)

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login')
      }, 3000)

    } catch (err) {
      console.error('Unexpected error:', err)
      setError('An unexpected error occurred. Please try again.')
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Animated Video Background */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="fixed inset-0 w-full h-full object-cover"
        style={{ zIndex: 0, filter: 'brightness(0.7)' }}
      >
        <source src="/assets/world-leaders-nuke.mp4" type="video/mp4" />
      </video>

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" style={{ zIndex: 1 }} />
      
      {/* Content */}
      <div 
        className="relative bg-slate-900/95 backdrop-blur-md p-8 rounded-lg w-full max-w-md border-4 border-emerald-500/50 shadow-2xl"
        style={{
          zIndex: 2,
          boxShadow: '0 0 40px rgba(16, 185, 129, 0.4), 0 0 80px rgba(16, 185, 129, 0.2), inset 0 2px 20px rgba(0,0,0,0.5)'
        }}
      >
        {/* Title */}
        <div className="text-center mb-6">
          <div className="text-5xl mb-4">🔑</div>
          <h1 className="text-3xl font-bold font-mono tracking-wider mb-2"
            style={{
              background: 'linear-gradient(to right, #10b981, #059669)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: '0 0 20px rgba(16, 185, 129, 0.5)',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.9))',
            }}
          >
            Create New Password
          </h1>
          <p className="text-slate-300 text-sm tracking-wide">
            Secure Your Commander Account
          </p>
          {emailFromUrl && (
            <p className="text-blue-400 text-xs mt-2 font-mono">
              {emailFromUrl}
            </p>
          )}
        </div>

        {/* Success Message */}
        {success ? (
          <div className="text-center">
            <div className="mb-6 bg-green-500/20 border-2 border-green-400 rounded-lg p-6 animate-pulse">
              <div className="text-5xl mb-3">✅ 🎉</div>
              <h3 className="text-2xl font-bold text-green-300 mb-2">Password Updated!</h3>
              <p className="text-slate-300 text-sm mb-3">
                Your password has been successfully reset.
              </p>
              <p className="text-slate-400 text-xs">
                Redirecting to login page...
              </p>
            </div>
            <div className="animate-spin inline-block w-8 h-8 border-4 border-green-400 border-t-transparent rounded-full"></div>
          </div>
        ) : (
          <>
            <div className="bg-emerald-500/10 border-2 border-emerald-400/40 rounded-lg p-4 mb-6">
              <p className="text-slate-300 text-sm text-center">
                Choose a strong password to protect your account.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-200 mb-2 tracking-wide">
                  New Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full px-4 py-3 bg-slate-800/80 border-2 border-slate-700 rounded-lg focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/50 text-white placeholder-slate-500 transition-all font-mono"
                  style={{
                    boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.4)'
                  }}
                  placeholder="••••••••"
                />
                <p className="text-slate-400 text-xs mt-1">
                  Minimum 6 characters
                </p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-200 mb-2 tracking-wide">
                  Confirm New Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full px-4 py-3 bg-slate-800/80 border-2 border-slate-700 rounded-lg focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/50 text-white placeholder-slate-500 transition-all font-mono"
                  style={{
                    boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.4)'
                  }}
                  placeholder="••••••••"
                />
              </div>

              {/* Password Strength Indicator */}
              {password && (
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <p className="text-xs text-slate-400 mb-2">Password Strength:</p>
                  <div className="flex gap-1">
                    <div className={`h-2 flex-1 rounded ${password.length >= 6 ? 'bg-red-500' : 'bg-slate-700'}`}></div>
                    <div className={`h-2 flex-1 rounded ${password.length >= 8 ? 'bg-yellow-500' : 'bg-slate-700'}`}></div>
                    <div className={`h-2 flex-1 rounded ${password.length >= 10 && /[A-Z]/.test(password) ? 'bg-green-500' : 'bg-slate-700'}`}></div>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {password.length < 6 && 'Too short'}
                    {password.length >= 6 && password.length < 8 && 'Weak'}
                    {password.length >= 8 && password.length < 10 && 'Fair'}
                    {password.length >= 10 && !/[A-Z]/.test(password) && 'Good'}
                    {password.length >= 10 && /[A-Z]/.test(password) && 'Strong'}
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-emerald-600 to-green-600 text-white font-bold py-4 px-4 rounded-lg hover:from-emerald-500 hover:to-green-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wider text-lg border-2 border-emerald-400/50 relative overflow-hidden group"
                style={{
                  boxShadow: '0 0 20px rgba(16, 185, 129, 0.5), 0 4px 12px rgba(0,0,0,0.5)',
                  textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                }}
              >
                <span className="relative z-10">
                  {loading ? '🔄 Updating...' : '🔒 Reset Password'}
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-green-400 opacity-0 group-hover:opacity-30 transition-opacity" />
              </button>
            </form>
          </>
        )}
      </div>
      
      {/* Animated glow effects */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl animate-pulse pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-green-500/20 rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDelay: '1s' }} />
    </div>
  )
}
